import { IProposal } from "./types/proposal.interface";
import { StateChecker } from "./services/state-checker";
import { Injectable } from "@angular/core";
import { PropertyAccessorService } from "../../shared/util/property-accessor.service";
import { IVendor } from './../../vendor/types';
import { IVersion } from "./types/version.interface";
import { Observable, BehaviorSubject, Subject } from "rxjs";
import { IPriceSummary } from "./types/price-summary.interface";
import { IPart } from './types/part.interface';
import { Collection } from "../../../../models/common/collection";
import { ICustomItem } from "./types/custom-item.interface";
import { IModification } from './types/modification.interface';
import { Part } from "../../../../models/common/part";
import { ICustomVersion } from "./types/custom-version.interface";

@Injectable({
  providedIn: 'root'
})
export class Proposal {

  public state$ = new BehaviorSubject<IProposal>({});
  public stepChange$ = new Subject<number>();
  public versionCartChange$ = new Subject<void>();
  public multiplier$ = new BehaviorSubject<boolean>(true);
  public availableParts$ = new BehaviorSubject<Part[]>([]);

  public events = {
    draftStatusChangeFromPopup$: new Subject<void>(),
    statusChangeFromPopup$: new Subject<void>(),
    parts: {
      added$: new Subject<IPart>(),
      removed$: new Subject<IPart>(),
      quantityUpdated$: new Subject<IPart>(),
      sidesUpdated$: new Subject<IPart>(),
    },
    customItems: {
      added$: new Subject<ICustomItem>(),
      removed$: new Subject<ICustomItem>(),
      quantityUpdated$: new Subject<ICustomItem>(),
      privacyChanged$: new Subject<ICustomItem>(),
    },
    modifications: {
      added$: new Subject<IModification>(),
      removed$: new Subject<IModification>(),
      quantityUpdated$: new Subject<IModification>()
    },
    assembled$: new Subject<boolean>(),
    orderedAssembled$: new Subject<boolean>(),
    taxRate$: new Subject<number>(),
    discount$: new Subject<{ versionIndex: number, discount: number }>(),
    collectionChanged$: new Subject<Collection>(),
    versionChanged$: new Subject<IVersion>()
  };

  private _state: IProposal = {};

  constructor(
    private stateChecker: StateChecker,
    private propertyAccessor: PropertyAccessorService,
  ) { }

  get state(): IProposal {
    return this._state;
  }

  get readonly(): boolean {
    return this._state.ordered && !window.location.href.includes('orderChange=yes');
  }

  /**
   * Determine whether path defined by property exists within proposal state.
   * Nested properties are separated by dot, ex. 'customer.name'.
   */
  public has(property: string): boolean {
    return this.propertyAccessor.has(property, this.state);
  }

  /**
   * Return the value within proposal state under given property path. Nested
   * properties are separated by dot, ex. 'customer.name'.
   */
  public get<T = any>(property: string, defaultValue = null): T {
    return this.propertyAccessor.get<T>(property, this.state, defaultValue);
  }

  /**
   * Determine whether proposal is in a given state. Available assertions are:
   * 
   * 1. 'finished'
   * 2. 'quick'
   * 3. 'saved'
   */
  public is(statement: string): boolean {
    return this.stateChecker.testState(this.state, statement);
  }

  /**
   * Determine whether proposal is not in a given state. For available checks,
   * see is() method.
   */
  public isNot(statement: string): boolean {
    return !this.is(statement);
  }

  /**
   * Merge the existing proposal state with the provided state.
   */
  public setState(state: any): void {
    this._state = { ...this._state, ...state };
    this.state$.next(this.state);
  }

  /**
   * Reinitialize the proposal state. Must be used whenever proposal Create/Edit
   * pages are opened.
   */
  public initializeState(): void {
    const date = new Date();
    const initialState = {
      date: `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`,
      notes: []
    };
    this._state = initialState;
    this.state$.next(this._state);
  }

  /**
   * Get the currently selected version or null if there are no versions.
   */
  public get activeVersion(): any {
    const versions = this.get<IVersion[]>('versions', []);
    const customVersions = this.get<IVersion[]>('customVersions', []);
    if (!versions.length && !customVersions.length) {
      return null;
    }
    const index = this.get<number>('activeVersionIndex', 0);
    return versions[index] ? versions[index] : customVersions[index - versions.length];
  }

  public customVersionActive() {
    return this.state.customVersions && this.state.customVersions.length && (this.state.activeVersionIndex >= this.state.versions.length);
  }

  /**
   * A convenient way to get a copy of versions since this is used in many places.
   */
  public cloneVersions(): IVersion[] {
    return JSON.parse(JSON.stringify(this.state.versions));
  }

  public cloneCustomVersions(): ICustomVersion[] {
    return JSON.parse(JSON.stringify(this.state.customVersions ? this.state.customVersions : []));
  }

  /**
   * Get the default discount. If default discount is defined on a customer we
   * use it, otherwise default discount is taken from provided vendor settings
   * depending on the customer type. If customer has no type set, default value
   * of 0 is returned.
   */
  public getDefaultVersionDiscount(vendor: IVendor): number {
    let discount = this.get<number>('customer.default_discount', 0);
    if (discount) return discount;
    const type = this.get<string>('customer.type');
    if (!type) {
      console.log('getDefaultVersionDiscount', 'Customer type not found');
      return 0;
    }
    if (type == 'Home Owner') {
      return parseFloat(vendor.pivot.home_owner_default_discount + '');
    }
    if (type == 'Builder') {
      return parseFloat(vendor.pivot.builder_default_discount + '');
    }
    if (type == 'Contractor') {
      return parseFloat(vendor.pivot.contractor_default_discount + '');
    }
    if (type == 'Architect') {
      return parseFloat(vendor.pivot.architect_default_discount + '');
    }
    if (type == 'Interior Designer') {
      return parseFloat(vendor.pivot.interior_designer_default_discount + '');
    }
    console.log('getDefaultVersionDiscount', 'Customer type unknown - ' + type);
    return 0;
  }

}

export function getInitialPriceSummary(): any {
  return {
    parts: 0,
    partsMultiplied: 0,
    drawers: 0,
    drawersMultiplied: 0,
    assemblyFee: 0,
    assemblyFeeMultiplied: 0,
    modifications: 0,
    modificationsMultiplied: 0,
    style: 0,
    styleMultiplied: 0,
    discount: 0,
    total: 0,
    totalMultiplied: 0,
    tax: 0,
    taxMultiplied: 0,
    final: 0,
    finalMultiplied: 0,
    versions: 0,
    versionsMultiplied: 0,
  }
}
